import {
  Avatar,
  Button,
  Col,
  Divider,
  Flex,
  Row,
  Switch,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useToken } from "../../hooks/useToken";
import {
  EIntegrationPlatform,
  TIntegration,
} from "../../models/integrations.model";
import {
  IntegrationList,
  useIntegrations,
} from "../../providers/IntegrationsProvider";
import { getConfig, TConfig } from "../../services/apiWorker";
import { deleteIntegration } from "../../services/integrations.service";
import { SettingsGooglePhotos } from "./SettingsGooglePhotos";

const ITEMS_PER_PAGE = 12;

export function IntegrationBox() {
  const { integrations, addIntegration, removeIntegration } = useIntegrations();
  const { token } = useToken();

  const [enabledIntegrations, setEnabledIntegrations] = useState<
    Record<string, boolean>
  >({});
  const [config, setConfig] = useState<TConfig>();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [integrationView, setIntegrationView] = useState<TIntegration>();

  useEffect(() => {
    const loadConfig = async () => {
      const configData = await getConfig();
      setConfig(configData);
      setIsLoading(false);
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (integrations.length) {
      const activeIntegrations = integrations.reduce((acc, integration) => {
        acc[integration.platform] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setEnabledIntegrations(activeIntegrations);
    }
  }, [integrations]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const paginatedIntegrations = IntegrationList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSwitchChange = async (
    platform: EIntegrationPlatform,
    checked: boolean
  ) => {
    if (checked) {
      const integration = IntegrationList.find(
        (integration) => integration.type === platform
      );
      if (!integration || !config) return;

      integration.openAuth(config);
    } else {
      deleteIntegration({
        platform,
        onSuccess: (data) => {
          removeIntegration(data);
        },
      });
    }
  };

  return (
    <>
      {integrationView &&
        integrationView.platform == EIntegrationPlatform.GOOGLE_PHOTOS && (
          <SettingsGooglePhotos
            integration={integrationView}
            onClose={() => setIntegrationView(undefined)}
          />
        )}
      <Flex
        style={{
          width: "100%",
        }}
      >
        <Flex vertical style={{ width: "100%" }}>
          <Typography
            style={{
              fontSize: token.fontSizeHeading4,
              fontWeight: token.fontWeightStrong,
              lineHeight: token.lineHeightHeading4,
            }}
          >
            Integrations
          </Typography>
          <Divider />
          <div
            style={{ width: "100%", height: "100%", overflow: "hidden auto" }}
          >
            <Row gutter={[24, 24]}>
              {paginatedIntegrations
                .sort(
                  (a, b) =>
                    Number(enabledIntegrations[b.type] || false) -
                    Number(enabledIntegrations[a.type] || false)
                )
                .map((item, index) => {
                  const integration = integrations.find(
                    (integration) => integration.platform === item.type
                  );

                  return (
                    <Col span={24} md={12} lg={12} xl={12} xxl={6} key={index}>
                      <Flex
                        vertical
                        style={{
                          border: `1px solid ${token.colorBorderSecondary}`,
                          padding: token.padding,
                          borderRadius: token.borderRadiusLG,
                        }}
                        gap="10px"
                      >
                        <Flex align="center" justify="space-between">
                          <Flex align="center" gap="10px">
                            <Avatar
                              src={item.icon}
                              shape="square"
                              style={{
                                width: "50px",
                                height: "50px",
                                backgroundColor: "white",
                                objectFit: "contain",
                              }}
                            ></Avatar>
                            <Flex vertical>
                              <Typography
                                style={{
                                  fontSize: token.fontSizeHeading4,
                                  fontWeight: token.fontWeightStrong,
                                  lineHeight: token.lineHeightHeading4,
                                }}
                              >
                                {item.title}
                              </Typography>
                              {integration?.metadataPublic?.requireAuth ===
                                true && (
                                <Typography
                                  style={{
                                    fontSize: token.fontSize,
                                    lineHeight: token.lineHeightHeading4,
                                    color: token.colorWarningText,
                                  }}
                                >
                                  Authentication required
                                </Typography>
                              )}
                            </Flex>
                          </Flex>
                          <Switch
                            checked={enabledIntegrations[item.type] || false}
                            onChange={(checked) =>
                              handleSwitchChange(item.type, checked)
                            }
                          />
                        </Flex>
                        <Flex style={{ width: "100%" }}>
                          <Typography
                            style={{
                              color: token.colorTextSecondary,
                              lineHeight: token.lineHeightLG,
                              fontSize: token.fontSizeLG,
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Flex>
                        <Flex
                          align="center"
                          justify="flex-end"
                          style={{ width: "100%", marginTop: "20px" }}
                        >
                          {integration?.metadataPublic?.requireAuth && (
                            <Button
                              type="primary"
                              onClick={() => {
                                if (config) {
                                  item.openAuth(config);
                                }
                              }}
                            >
                              Authenticate
                            </Button>
                          )}
                          <Button
                            type="text"
                            onClick={() => {
                              setIntegrationView(
                                integrations.find(
                                  (integration) =>
                                    integration.platform == item.type
                                )
                              );
                            }}
                          >
                            View Integration
                          </Button>
                        </Flex>
                      </Flex>
                    </Col>
                  );
                })}
            </Row>
          </div>
          {/* <Flex
            align="center"
            justify="flex-end"
            style={{
              width: "100%",
              height: "100%",
              marginTop: "20px",
            }}
          >
            <Pagination
              current={currentPage}
              total={IntegrationList.length}
              pageSize={ITEMS_PER_PAGE}
              onChange={(page) => setCurrentPage(page)}
            />
          </Flex> */}
        </Flex>
      </Flex>
    </>
  );
}
